import { Button, Input, Label, TextArea, TextField } from "@heroui/react";
import {
	type AdminCharacter,
	type AdminLocation,
	CHAIN_STRATEGY_VALUES,
	CONVERGENCE_STRATEGY_VALUES,
	ENDING_RARITY_VALUES,
	type EpisodePlan,
	FLAG_TYPE_VALUES,
	FlagConditionCmp,
	FlagEffectOp,
	FlagType,
	type PlanNode,
	RENDER_MODE_VALUES,
	RenderMode,
} from "@storyline/shared";
import { useState } from "react";
import Pill from "@/src/common/components/pill";
import SectionCard from "@/src/common/components/section-card";
import SelectField, { type SelectOption } from "@/src/common/components/select-field";
import {
	CHAIN_STRATEGY_OPTIONS,
	CONVERGENCE_OPTIONS,
	COUNTDOWN_OPTIONS,
	FLAG_CMP_OPTIONS,
	FLAG_OP_OPTIONS,
	FLAG_TYPE_OPTIONS,
	RARITY_OPTIONS,
	RENDER_MODE_OPTIONS,
} from "@/src/common/constants/studio-labels";
import { narrowEnum } from "@/src/common/utils/narrow-enum";
import ClauseEditor from "@/src/series/components/clause-editor";
import {
	addFlag,
	addRule,
	removeFlag,
	removeRule,
	setChoiceEffects,
	setPlanField,
	setRuleConditions,
	updateChoice,
	updateDecision,
	updateEnding,
	updateNode,
	updateRule,
	updateShot,
} from "@/src/series/utils/plan-editor";

type PropositionEditorProps = {
	plan: EpisodePlan;
	characters: AdminCharacter[];
	locations: AdminLocation[];
	isCommitting: boolean;
	error: string | null;
	onCommit: (plan: EpisodePlan) => void;
	onCancel: () => void;
};

const outcomeLabel = (node: PlanNode): string => {
	if (node.decision) {
		return "Décision";
	}
	if (node.nextNodeIndex !== null) {
		return "Enchaînement direct";
	}
	if (node.endingIndex !== null) {
		return "Fin statique";
	}
	return "Terminal (résolu par règles)";
};

const PropositionEditor: React.FC<PropositionEditorProps> = ({
	plan: initialPlan,
	characters,
	locations,
	isCommitting,
	error,
	onCommit,
	onCancel,
}) => {
	const [plan, setPlan] = useState<EpisodePlan>(initialPlan);
	const [expandedEndActions, setExpandedEndActions] = useState<Set<string>>(new Set());

	const nodeOptions: SelectOption[] = plan.nodes.map((node, index) => ({ value: String(index), label: `${index + 1}. ${node.title}` }));
	const endingOptions: SelectOption[] = plan.endings.map((ending, index) => ({
		value: String(index),
		label: `${index + 1}. ${ending.name}`,
	}));
	const locationOptions: SelectOption[] = [
		{ value: "-1", label: "Aucun lieu" },
		...locations.map((location, index) => ({ value: String(index), label: location.name })),
	];

	return (
		<div className="flex flex-col gap-5">
			{error ? <p className="rounded-lg border border-danger/40 bg-glass p-3 text-danger text-sm">{error}</p> : null}

			<SectionCard title="Métadonnées de l'épisode">
				<div className="flex flex-col gap-4">
					<TextField value={plan.title} onChange={(value) => setPlan(setPlanField(plan, { title: value }))}>
						<Label>Titre</Label>
						<Input />
					</TextField>
					<TextField value={plan.synopsis} onChange={(value) => setPlan(setPlanField(plan, { synopsis: value }))}>
						<Label>Synopsis</Label>
						<TextArea rows={2} />
					</TextField>
					<SelectField
						label="Nœud d'entrée"
						options={nodeOptions}
						value={String(plan.entryNodeIndex)}
						onChange={(value) => setPlan(setPlanField(plan, { entryNodeIndex: Number.parseInt(value, 10) }))}
					/>
				</div>
			</SectionCard>

			<SectionCard
				title={`Flags (${plan.flags.length})`}
				actions={
					<Button
						size="sm"
						variant="secondary"
						onPress={() => setPlan(addFlag(plan, { name: `flag_${plan.flags.length + 1}`, type: FlagType.BOOLEAN, initialValue: false }))}
					>
						Ajouter un flag
					</Button>
				}
			>
				<div className="flex flex-col gap-2">
					{plan.flags.map((flag, flagIndex) => (
						<div key={`flag-${flagIndex}`} className="flex flex-wrap items-center gap-2">
							<TextField
								value={flag.name}
								onChange={(value) =>
									setPlan({ ...plan, flags: plan.flags.map((item, index) => (index === flagIndex ? { ...item, name: value } : item)) })
								}
								className="w-40"
							>
								<Input placeholder="nom" />
							</TextField>
							<SelectField
								options={FLAG_TYPE_OPTIONS}
								value={flag.type}
								onChange={(value) => {
									const type = narrowEnum(FLAG_TYPE_VALUES, value, FlagType.BOOLEAN);
									setPlan({
										...plan,
										flags: plan.flags.map((item, index) =>
											index === flagIndex ? { ...item, type, initialValue: type === FlagType.BOOLEAN ? false : 0 } : item,
										),
									});
								}}
							/>
							<Pill>initial : {String(flag.initialValue)}</Pill>
							<Button size="sm" variant="ghost" className="text-danger" onPress={() => setPlan(removeFlag(plan, flagIndex))}>
								Supprimer
							</Button>
						</div>
					))}
				</div>
			</SectionCard>

			<SectionCard
				title={`Nœuds (${plan.nodes.length})`}
				description="Affinez titres, plans, choix et effets — la structure vient du copilote."
			>
				<div className="flex flex-col gap-4">
					{plan.nodes.map((node, nodeIndex) => (
						<div key={`node-${nodeIndex}`} className="flex flex-col gap-3 rounded-xl border border-border-soft bg-surface-2 p-4">
							<div className="flex flex-wrap items-center justify-between gap-2">
								<TextField
									value={node.title}
									onChange={(value) => setPlan(updateNode(plan, nodeIndex, { title: value }))}
									className="flex-1"
								>
									<Label>{`Nœud ${nodeIndex + 1}`}</Label>
									<Input />
								</TextField>
								<Pill tone="accent">{outcomeLabel(node)}</Pill>
							</div>

							<SelectField
								label="Stratégie de convergence"
								options={[{ value: "", label: "Aucune (un seul parent)" }, ...CONVERGENCE_OPTIONS]}
								value={node.convergenceStrategy ?? ""}
								onChange={(value) =>
									setPlan(
										updateNode(plan, nodeIndex, {
											convergenceStrategy:
												value === "" ? null : narrowEnum(CONVERGENCE_STRATEGY_VALUES, value, CONVERGENCE_STRATEGY_VALUES[0]),
										}),
									)
								}
							/>

							<div className="flex flex-col gap-3">
								<span className="font-semibold text-muted text-xs uppercase tracking-wide">Plans ({node.shots.length})</span>
								{node.shots.map((shot, shotIndex) => (
									<div
										key={`shot-${nodeIndex}-${shotIndex}`}
										className="flex flex-col gap-2 rounded-lg border border-border-soft bg-glass p-3"
									>
										<TextField
											value={shot.purpose}
											onChange={(value) => setPlan(updateShot(plan, nodeIndex, shotIndex, { purpose: value }))}
										>
											<Label>Intention</Label>
											<Input placeholder="hook, révélation…" />
										</TextField>
										<TextField value={shot.action} onChange={(value) => setPlan(updateShot(plan, nodeIndex, shotIndex, { action: value }))}>
											<Label>Action (prompt image)</Label>
											<TextArea rows={2} />
										</TextField>
										{shot.renderMode === RenderMode.PREMIUM_VIDEO ||
										shot.endAction !== null ||
										expandedEndActions.has(`${nodeIndex}-${shotIndex}`) ? (
											<TextField
												value={shot.endAction ?? ""}
												onChange={(value) => setPlan(updateShot(plan, nodeIndex, shotIndex, { endAction: value === "" ? null : value }))}
											>
												<Label>{shot.renderMode === RenderMode.PREMIUM_VIDEO ? "État de fin (premium)" : "État de fin (optionnel)"}</Label>
												<TextArea rows={2} placeholder="Décris ce qu'on voit à la FIN du plan, différent du début" />
											</TextField>
										) : (
											<Button
												size="sm"
												variant="ghost"
												className="self-start text-muted"
												onPress={() => setExpandedEndActions((previous) => new Set(previous).add(`${nodeIndex}-${shotIndex}`))}
											>
												+ État de fin (optionnel)
											</Button>
										)}
										<div className="grid gap-2 md:grid-cols-2">
											<SelectField
												label="Rendu"
												options={RENDER_MODE_OPTIONS}
												value={shot.renderMode}
												onChange={(value) =>
													setPlan(
														updateShot(plan, nodeIndex, shotIndex, { renderMode: narrowEnum(RENDER_MODE_VALUES, value, shot.renderMode) }),
													)
												}
											/>
											<SelectField
												label="Chaînage"
												options={CHAIN_STRATEGY_OPTIONS}
												value={shot.chainStrategy}
												onChange={(value) =>
													setPlan(
														updateShot(plan, nodeIndex, shotIndex, {
															chainStrategy: narrowEnum(CHAIN_STRATEGY_VALUES, value, shot.chainStrategy),
														}),
													)
												}
											/>
											<TextField
												value={String(shot.durationSeconds)}
												onChange={(value) =>
													setPlan(
														updateShot(plan, nodeIndex, shotIndex, { durationSeconds: Number.parseFloat(value) || shot.durationSeconds }),
													)
												}
											>
												<Label>Durée (s)</Label>
												<Input type="number" />
											</TextField>
											<SelectField
												label="Lieu"
												options={locationOptions}
												value={String(shot.locationIndex ?? -1)}
												onChange={(value) => {
													const index = Number.parseInt(value, 10);
													setPlan(updateShot(plan, nodeIndex, shotIndex, { locationIndex: index < 0 ? null : index }));
												}}
											/>
										</div>
										<div className="flex flex-wrap gap-1">
											{characters.map((character, characterIndex) => {
												const active = shot.characterIndexes.includes(characterIndex);
												return (
													<Button
														key={character.id}
														size="sm"
														variant={active ? "secondary" : "ghost"}
														onPress={() =>
															setPlan(
																updateShot(plan, nodeIndex, shotIndex, {
																	characterIndexes: active
																		? shot.characterIndexes.filter((index) => index !== characterIndex)
																		: [...shot.characterIndexes, characterIndex],
																}),
															)
														}
													>
														{active ? "✓ " : ""}
														{character.name}
													</Button>
												);
											})}
										</div>
									</div>
								))}
							</div>

							{node.decision ? (
								<div className="flex flex-col gap-2 rounded-lg border border-accent-soft bg-glass p-3">
									<span className="font-semibold text-muted text-xs uppercase tracking-wide">Décision</span>
									<TextField
										value={node.decision.question}
										onChange={(value) => setPlan(updateDecision(plan, nodeIndex, { question: value }))}
									>
										<Label>Question</Label>
										<Input />
									</TextField>
									<SelectField
										label="Compte à rebours"
										options={COUNTDOWN_OPTIONS}
										value={String(node.decision.timeoutSeconds)}
										onChange={(value) => setPlan(updateDecision(plan, nodeIndex, { timeoutSeconds: Number.parseInt(value, 10) }))}
									/>
									{node.decision.choices.map((choice, choiceIndex) => (
										<div
											key={`choice-${nodeIndex}-${choiceIndex}`}
											className="flex flex-col gap-2 rounded-lg border border-border-soft bg-surface-2 p-2"
										>
											<div className="flex flex-wrap items-center gap-2">
												<TextField
													value={choice.label}
													onChange={(value) => setPlan(updateChoice(plan, nodeIndex, choiceIndex, { label: value }))}
													className="flex-1"
												>
													<Input placeholder="Libellé du choix" />
												</TextField>
												<SelectField
													options={nodeOptions}
													value={String(choice.nextNodeIndex)}
													onChange={(value) =>
														setPlan(updateChoice(plan, nodeIndex, choiceIndex, { nextNodeIndex: Number.parseInt(value, 10) }))
													}
												/>
											</div>
											<div className="flex flex-col gap-1">
												{choice.effects.map((effect, effectIndex) => (
													<ClauseEditor
														key={`effect-${nodeIndex}-${choiceIndex}-${effectIndex}`}
														flags={plan.flags}
														flag={effect.flag}
														operator={effect.op}
														operatorOptions={FLAG_OP_OPTIONS}
														value={effect.value}
														onChange={(next) =>
															setPlan(
																setChoiceEffects(
																	plan,
																	nodeIndex,
																	choiceIndex,
																	choice.effects.map((item, index) =>
																		index === effectIndex
																			? {
																					flag: next.flag,
																					op: narrowEnum([FlagEffectOp.SET, FlagEffectOp.ADD], next.operator, FlagEffectOp.SET),
																					value: next.value,
																				}
																			: item,
																	),
																),
															)
														}
														onRemove={() =>
															setPlan(
																setChoiceEffects(
																	plan,
																	nodeIndex,
																	choiceIndex,
																	choice.effects.filter((_, index) => index !== effectIndex),
																),
															)
														}
													/>
												))}
												{plan.flags.length > 0 ? (
													<Button
														size="sm"
														variant="ghost"
														onPress={() =>
															setPlan(
																setChoiceEffects(plan, nodeIndex, choiceIndex, [
																	...choice.effects,
																	{
																		flag: plan.flags[0].name,
																		op: FlagEffectOp.SET,
																		value: plan.flags[0].type === FlagType.BOOLEAN ? true : 1,
																	},
																]),
															)
														}
													>
														+ effet
													</Button>
												) : null}
											</div>
										</div>
									))}
									<SelectField
										label="Choix par défaut"
										options={node.decision.choices.map((choice, choiceIndex) => ({ value: String(choiceIndex), label: choice.label }))}
										value={String(node.decision.defaultChoiceIndex)}
										onChange={(value) => setPlan(updateDecision(plan, nodeIndex, { defaultChoiceIndex: Number.parseInt(value, 10) }))}
									/>
								</div>
							) : node.nextNodeIndex !== null ? (
								<SelectField
									label="Enchaîne vers"
									options={nodeOptions}
									value={String(node.nextNodeIndex)}
									onChange={(value) => setPlan(updateNode(plan, nodeIndex, { nextNodeIndex: Number.parseInt(value, 10) }))}
								/>
							) : node.endingIndex !== null ? (
								<SelectField
									label="Fin statique"
									options={endingOptions}
									value={String(node.endingIndex)}
									onChange={(value) => setPlan(updateNode(plan, nodeIndex, { endingIndex: Number.parseInt(value, 10) }))}
								/>
							) : null}
						</div>
					))}
				</div>
			</SectionCard>

			<SectionCard title={`Fins (${plan.endings.length})`}>
				<div className="flex flex-col gap-2">
					{plan.endings.map((ending, endingIndex) => (
						<div key={`ending-${endingIndex}`} className="flex flex-wrap items-center gap-2">
							<TextField
								value={ending.name}
								onChange={(value) => setPlan(updateEnding(plan, endingIndex, { name: value }))}
								className="flex-1"
							>
								<Input placeholder="Nom de la fin" />
							</TextField>
							<SelectField
								options={RARITY_OPTIONS}
								value={ending.rarity}
								onChange={(value) =>
									setPlan(updateEnding(plan, endingIndex, { rarity: narrowEnum(ENDING_RARITY_VALUES, value, ending.rarity) }))
								}
							/>
						</div>
					))}
				</div>
			</SectionCard>

			<SectionCard
				title={`Règles de fin (${plan.endingRules.length})`}
				description="Conditions vides = règle par défaut. Une seule par défaut, priorités uniques."
				actions={
					<Button
						size="sm"
						variant="secondary"
						onPress={() => setPlan(addRule(plan, { endingIndex: 0, priority: plan.endingRules.length, conditions: [] }))}
					>
						Ajouter une règle
					</Button>
				}
			>
				<div className="flex flex-col gap-3">
					{plan.endingRules.map((rule, ruleIndex) => (
						<div key={`rule-${ruleIndex}`} className="flex flex-col gap-2 rounded-lg border border-border-soft bg-glass p-3">
							<div className="flex flex-wrap items-center gap-2">
								<SelectField
									label="Fin"
									options={endingOptions}
									value={String(rule.endingIndex)}
									onChange={(value) => setPlan(updateRule(plan, ruleIndex, { endingIndex: Number.parseInt(value, 10) }))}
								/>
								<TextField
									value={String(rule.priority)}
									onChange={(value) => setPlan(updateRule(plan, ruleIndex, { priority: Number.parseInt(value, 10) || 0 }))}
									className="w-28"
								>
									<Label>Priorité</Label>
									<Input type="number" />
								</TextField>
								<Button size="sm" variant="ghost" className="text-danger" onPress={() => setPlan(removeRule(plan, ruleIndex))}>
									Supprimer
								</Button>
							</div>
							<div className="flex flex-col gap-1">
								{rule.conditions.map((condition, conditionIndex) => (
									<ClauseEditor
										key={`condition-${ruleIndex}-${conditionIndex}`}
										flags={plan.flags}
										flag={condition.flag}
										operator={condition.cmp}
										operatorOptions={FLAG_CMP_OPTIONS}
										value={condition.value}
										onChange={(next) =>
											setPlan(
												setRuleConditions(
													plan,
													ruleIndex,
													rule.conditions.map((item, index) =>
														index === conditionIndex
															? {
																	flag: next.flag,
																	cmp: narrowEnum(
																		[FlagConditionCmp.EQ, FlagConditionCmp.GTE, FlagConditionCmp.LTE],
																		next.operator,
																		FlagConditionCmp.EQ,
																	),
																	value: next.value,
																}
															: item,
													),
												),
											)
										}
										onRemove={() =>
											setPlan(
												setRuleConditions(
													plan,
													ruleIndex,
													rule.conditions.filter((_, index) => index !== conditionIndex),
												),
											)
										}
									/>
								))}
								{plan.flags.length > 0 ? (
									<Button
										size="sm"
										variant="ghost"
										onPress={() =>
											setPlan(
												setRuleConditions(plan, ruleIndex, [
													...rule.conditions,
													{ flag: plan.flags[0].name, cmp: FlagConditionCmp.EQ, value: plan.flags[0].type === FlagType.BOOLEAN ? true : 1 },
												]),
											)
										}
									>
										+ condition
									</Button>
								) : (
									<Pill tone="warning">Règle par défaut (aucune condition)</Pill>
								)}
							</div>
						</div>
					))}
				</div>
			</SectionCard>

			<div className="flex items-center justify-end gap-2">
				<Button variant="ghost" className="text-muted" onPress={onCancel}>
					Annuler
				</Button>
				<Button isDisabled={isCommitting} onPress={() => onCommit(plan)}>
					{isCommitting ? "Création…" : "Créer l'épisode"}
				</Button>
			</div>
		</div>
	);
};

export default PropositionEditor;
